import React, { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { formatAdminPaymentProofSubtitle } from '../../utils/paymentProofDisplay'
import PaymentProofFileViewer from './PaymentProofFileViewer'
import CollapsibleSection from '../CollapsibleSection'

function SectionLoadState({ status, error, onRetry }) {
  if (status === 'error') {
    return (
      <Alert
        severity="error"
        action={
          onRetry ? (
            <Button color="inherit" size="small" onClick={onRetry}>
              Retry
            </Button>
          ) : null
        }
      >
        {error || 'Failed to load this section'}
      </Alert>
    )
  }
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
      <CircularProgress size={24} />
    </Box>
  )
}

function formatAmount(value) {
  const n = Number(value ?? 0)
  return `৳${n.toFixed(2)}`
}

function formatVarianceAmount(value) {
  const paisa = Math.abs(toPaisa(value))
  const whole = Math.floor(paisa / 100)
  const frac = paisa % 100
  const grouped = whole.toLocaleString('en-US')
  if (frac === 0) return grouped
  return `${grouped}.${String(frac).padStart(2, '0')}`
}

function toPaisa(value) {
  const n = Number(value ?? 0)
  return Number.isFinite(n) ? Math.round(n * 100) : 0
}

/**
 * Warning when a pending request is at least 50% below or 50% above the
 * remaining balance. Overpayment inside that band is allowed, and Verify
 * stays available either way.
 */
export function paymentAmountVarianceWarning(summary, req) {
  if (!summary || summary.remaining == null) return null
  const designated = toPaisa(summary.remaining)
  const entered = toPaisa(req?.requested_amount)
  if (!Number.isFinite(designated) || designated < 0 || entered <= 0) return null
  const low = Math.round(designated * 0.5)
  const high = Math.round(designated * 1.5)
  const difference = formatVarianceAmount((entered - designated) / 100)
  const amount = formatVarianceAmount(entered / 100)
  if (designated === 0 || entered >= high) {
    return `Amount paid is ${difference} tk more than the total amount. Confirm ${amount} is correct.`
  }
  if (entered <= low) {
    return `Amount paid is ${difference} tk less than the total amount. Confirm ${amount} is correct.`
  }
  return null
}

function formatDate(value, withTime = true) {
  if (!value) return '—'
  try {
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return '—'
    return withTime ? d.toLocaleString() : d.toLocaleDateString()
  } catch {
    return '—'
  }
}

function statusChip(status) {
  const upper = String(status || '').toUpperCase()
  if (upper === 'VERIFIED') return { label: 'Verified', color: 'success' }
  if (upper === 'REJECTED') return { label: 'Rejected', color: 'error' }
  if (upper === 'PENDING') return { label: 'Pending', color: 'warning' }
  return { label: status || '—', color: 'default' }
}

function rejectionReasonForRequest(req) {
  const upper = String(req?.status || '').toUpperCase()
  if (upper !== 'REJECTED') return null
  const reason = String(req?.rejection_reason ?? '').trim()
  return reason || null
}

function PaymentRequestProofsDialog({ request, onClose }) {
  const proofs = Array.isArray(request?.proofs) ? request.proofs : []
  const [index, setIndex] = useState(0)

  useEffect(() => {
    setIndex(0)
  }, [request?.id])

  const proof = proofs[index] || null
  const fileUrl = proof?.file_url || ''
  const open = Boolean(request)

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {proofs.length > 1
            ? `Payment proofs (${index + 1} of ${proofs.length})`
            : 'Payment proof'}
          {request?.id != null ? ` · #${request.id}` : ''}
        </Typography>
        <IconButton onClick={onClose} aria-label="Close payment proof">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {proof ? (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {formatAdminPaymentProofSubtitle(proof)}
            </Typography>
            {!fileUrl ? (
              <Typography variant="body2" color="text.secondary">
                {String(proof.payment_method || '').toUpperCase() === 'EXISTING_BALANCE'
                  ? 'Existing balance'
                  : 'This proof has no file to view.'}
              </Typography>
            ) : (
              <PaymentProofFileViewer
                fileUrl={fileUrl}
                title={`Payment proof ${proof.id}`}
                maxHeight="70vh"
              />
            )}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No proofs on this request.
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {proofs.length > 1 ? (
            <>
              <Button disabled={index === 0} onClick={() => setIndex((current) => current - 1)}>
                Previous
              </Button>
              <Button
                disabled={index >= proofs.length - 1}
                onClick={() => setIndex((current) => current + 1)}
              >
                Next
              </Button>
            </>
          ) : null}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {fileUrl ? (
            <Button component="a" href={fileUrl} target="_blank" rel="noopener noreferrer">
              Open file
            </Button>
          ) : null}
          <Button onClick={onClose} color="inherit">
            Close
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  )
}

export default function PaymentInformationSection({
  summary,
  requests = [],
  hasLegacyFiles = false,
  feedback,
  onDismissFeedback,
  canVerify,
  actionRequestId,
  rejectSubmitting,
  onVerify,
  onReject,
  canEditRejected = false,
  onEditRejected,
  canReverse = false,
  reverseSubmitting = false,
  onReverse,
  onExpandedChange,
  sectionStatus = 'ready',
  sectionError = null,
  onRetrySection,
}) {
  const [viewingRequestId, setViewingRequestId] = useState(null)
  const showActions = canVerify || canReverse

  const sorted = [...requests].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  )
  const viewingRequest = sorted.find((req) => req.id === viewingRequestId) || null
  const showActionsColumn = showActions || sorted.some((req) => (req.proofs || []).length > 0)

  return (
    <CollapsibleSection title="Payment Information" onExpandedChange={onExpandedChange}>
        {sectionStatus !== 'ready' ? (
          <SectionLoadState
            status={sectionStatus}
            error={sectionError}
            onRetry={onRetrySection}
          />
        ) : (
          <>
        {feedback ? (
          <Alert severity={feedback.severity} sx={{ mb: 2 }} onClose={onDismissFeedback}>
            {feedback.message}
          </Alert>
        ) : null}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
            gap: 2,
            mb: 3,
          }}
        >
          {[
            { label: 'Total receivable', value: summary?.payable, accent: '#e90000' },
            { label: 'Received', value: summary?.received, accent: '#0F766E' },
            { label: 'Remaining', value: summary?.remaining, accent: '#B45309' },
            { label: 'Requested', value: summary?.requested, accent: '#11809A' },
          ].map(({ label, value, accent }) => (
            <Box
              key={label}
              sx={{
                p: 2,
                borderRadius: 2,
                border: '1px solid #e8e8e8',
                bgcolor: '#fafafa',
              }}
            >
              <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>
                {label}
              </Typography>
              <Typography sx={{ fontWeight: 600, color: accent, fontSize: '1.1rem' }}>
                {formatAmount(value)}
              </Typography>
            </Box>
          ))}
        </Box>
        {sorted.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {hasLegacyFiles
              ? 'Uploaded files for this order are listed under Payment Proof.'
              : 'No payment proof requests yet.'}
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8f9fb' }}>
                  <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Requested</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Received</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">Proof count</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Upload date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, minWidth: 160 }}>Reason</TableCell>
                  {showActionsColumn ? (
                    <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                  ) : null}
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.map((req) => {
                  const chip = statusChip(req.status)
                  const isPending = String(req.status || '').toUpperCase() === 'PENDING'
                  const isRejected = String(req.status || '').toUpperCase() === 'REJECTED'
                  const editedAfterRejection = Boolean(req.edited_after_rejection)
                  const proofCount = Array.isArray(req.proofs) ? req.proofs.length : 0
                  const rowBusy = actionRequestId === req.id
                  const rejectionReason = rejectionReasonForRequest(req)
                  const amountWarning =
                    isPending && paymentAmountVarianceWarning(summary, req)
                  return (
                    <TableRow
                      key={req.id}
                      sx={isPending ? { bgcolor: 'rgba(255, 243, 205, 0.35)' } : undefined}
                    >
                      <TableCell>#{req.id}</TableCell>
                      <TableCell align="right">{formatAmount(req.requested_amount)}</TableCell>
                      <TableCell align="right">{formatAmount(req.received_amount)}</TableCell>
                      <TableCell align="center">{proofCount}</TableCell>
                      <TableCell>{formatDate(req.created_at, true)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                          <Chip label={chip.label} color={chip.color} size="small" variant="outlined" />
                          {isPending && editedAfterRejection ? (
                            <Chip label="Edited" size="small" color="info" variant="outlined" />
                          ) : null}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {rejectionReason ? (
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#b91c1c',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              maxWidth: 280,
                            }}
                          >
                            {rejectionReason}
                          </Typography>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      {showActionsColumn ? (
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                            {amountWarning ? (
                              <Typography
                                variant="caption"
                                sx={{ width: '100%', textAlign: 'right', color: '#B45309' }}
                              >
                                {amountWarning}
                              </Typography>
                            ) : null}
                            {proofCount > 0 ? (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => setViewingRequestId(req.id)}
                              >
                                {proofCount === 1 ? 'View proof' : `View proofs (${proofCount})`}
                              </Button>
                            ) : null}
                            {isPending && canVerify ? (
                              <>
                                <Button
                                  size="small"
                                  variant="contained"
                                  disabled={rowBusy || rejectSubmitting}
                                  onClick={() => onVerify(req.id)}
                                  sx={{ bgcolor: '#11809A', '&:hover': { bgcolor: '#0f6d82' } }}
                                >
                                  {rowBusy ? 'Verifying…' : 'Verify'}
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  disabled={rowBusy || rejectSubmitting}
                                  onClick={() => onReject(req)}
                                >
                                  Reject
                                </Button>
                              </>
                            ) : isRejected && canEditRejected && onEditRejected ? (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => onEditRejected(req)}
                              >
                                Edit
                              </Button>
                            ) : !isPending && canReverse && onReverse ? (
                              <Button
                                size="small"
                                variant="outlined"
                                color="warning"
                                disabled={reverseSubmitting || rejectSubmitting}
                                onClick={() => onReverse(req)}
                              >
                                Reverse to pending
                              </Button>
                            ) : proofCount === 0 ? (
                              '—'
                            ) : null}
                          </Box>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <PaymentRequestProofsDialog
          request={viewingRequest}
          onClose={() => setViewingRequestId(null)}
        />
          </>
        )}
    </CollapsibleSection>
  )
}
